import { Settings, useSaveSettings, WMSInfo, WMSLayerInfo } from '@ais-ui/websockets';
import { useSettings } from '../Settings/SettingsProvider';
import { useMemo } from 'react';
import {
  LayerItem,
  useLayers,
  useWmsToTileLayer,
} from '@ais-ui/react-openlayers';
import { useToast } from '@ais-ui/ui-core';
import { useLocalization } from '@ais-ui/locale-management';

interface WMSSettings {
  wmsInfos: WMSInfo[];
  enabledWmsLayers: EnabledWMSLayer[];
}

export interface EnabledWMSLayer {
  wmsInfo: WMSInfo;
  wmsLayerInfo: WMSLayerInfo;
}

export const getWmsLayersFromSettings = (settings: Settings): WMSInfo[] => {
  const wmsLayers: WMSInfo[] =
    settings && settings.wmsSettings ? settings.wmsSettings : [];

  // This is only here for backwards compatibility
  wmsLayers.forEach((layer) => {
    if ('singletile' in layer && !('singleTile' in layer)) {
      layer.singleTile = layer.singletile;
    }
    if ('trans' in layer && !('transparency' in layer)) {
      layer.transparency = layer.trans;
    }
  });

  return settings && settings.wmsSettings ? settings.wmsSettings : [];
};

function getEnabledWMSLayers(wmsLayers: WMSInfo[]): EnabledWMSLayer[] {
  const enabledWMS = wmsLayers
    .filter((wmsLayer) => wmsLayer.enabled)
    .flatMap((wmsLayer) =>
      (wmsLayer.layers ?? [])
        .filter((layer) => layer.enabled)
        .map((layer) => ({ wmsInfo: wmsLayer, wmsLayerInfo: layer }))
    );

  return enabledWMS;
}

export const useWMSSettings = () => {
  const { settings, dataUpdatedAt } = useSettings();

  const computedSettings = useMemo(() => {
    const fetchedWmsLayers = getWmsLayersFromSettings(settings);
    const newEnabledWmsLayers = getEnabledWMSLayers(fetchedWmsLayers);
    return {
      wmsInfos: fetchedWmsLayers,
      enabledWmsLayers: newEnabledWmsLayers,
    };
    // We have to use dataUpdatedAt here to make sure that the settings are updated because React
    //  doesn't see changes
  }, [settings, dataUpdatedAt]);

  return computedSettings;
};

export const addWmsToSettings = (
  wmsLayer: WMSInfo,
  settings: Settings
): Settings => {
  const wmsLayers = getWmsLayersFromSettings(settings);

  wmsLayers.push(wmsLayer);

  const settingsToSave = {
    ...settings,
    wmsSettings: wmsLayers,
  };

  return settingsToSave;
};

export const UpdateWmsToSettings = (
  updatedWmsLayer: WMSInfo,
  settings: Settings
): Settings => {
  const wmsLayers = getWmsLayersFromSettings(settings);

  const layerIndex = wmsLayers.findIndex(
    (layer) => layer.id === updatedWmsLayer.id
  );

  if (layerIndex !== -1) {
    wmsLayers[layerIndex] = updatedWmsLayer;
  } else {
    return settings;
  }

  const settingsToSave = {
    ...settings,
    wmsSettings: wmsLayers,
  };

  return settingsToSave;
};

export const updateActiveBaseMapInSettings = (
  activeBaseMap: string,
  settings: Settings
): Settings => {
  const settingsToSave = {
    ...settings,
    'ribbonPlugin.settings': {
      ...settings['ribbonPlugin.settings'],
      active_base_map: activeBaseMap,
    },
  };

  return settingsToSave;
};

export const deleteWmsToSettings = (
  wmsLayer: WMSInfo,
  settings: Settings
): Settings => {
  const wmsLayers = getWmsLayersFromSettings(settings);

  const filteredWmsLayers = wmsLayers.filter(
    (layer) => layer.id !== wmsLayer.id
  );

  const settingsToSave = {
    ...settings,
    wmsSettings: filteredWmsLayers,
  };

  return settingsToSave;
};

export const ToggleBaseMapInSettings = (
  settings: Settings,
  wmsSettings: WMSSettings,
  wmsLayerId: string,
  isBase: boolean
): Settings => {
  const updatedWmsInfos = wmsSettings.wmsInfos.map((wmsLayer) => {
    if (wmsLayer.layers) {
      return {
        ...wmsLayer,
        layers: wmsLayer.layers.map((layer) =>
          layer.id === wmsLayerId ? { ...layer, isBase: isBase } : layer
        ),
      };
    }
    return wmsLayer;
  });

  return {
    ...settings,
    wmsSettings: updatedWmsInfos,
  };
};

export const ToggleWmsLayerInSettings = (
  wmsLayers: WMSInfo[],
  wmsLayerId: string,
  layerName: string,
  enabled: boolean,
  settings: Settings
): Settings => {
  const layerIndex = wmsLayers.findIndex((layer) => layer.id === wmsLayerId);

  if (layerIndex !== -1) {
    const wmsLayer = wmsLayers[layerIndex];
    const matchingLayerIndex = wmsLayer?.layers?.findIndex(
      (layer) => layer.name === layerName
    );
    if (matchingLayerIndex === -1) {
      return settings;
    }

    const updatedWmsLayer = {
      ...wmsLayer,
      layers: wmsLayer.layers?.map((layer, index) =>
        index === matchingLayerIndex ? { ...layer, enabled: enabled } : layer
      ),
    };
    const updatedWmsLayers = wmsLayers.map((layer, index) =>
      index === layerIndex ? updatedWmsLayer : layer
    );

    const settingsToSave = {
      ...settings,
      wmsSettings: updatedWmsLayers,
    };

    return settingsToSave;
  } else {
    return settings;
  }
};

export const isWmsLayerShownOnStartUp = (
  settings: Settings,
  wmsLayerId: string
): boolean => {
  return (
    settings?.wmsSettings?.some((wmsLayer) =>
      wmsLayer?.layers?.some(
        (layer) => layer?.id === wmsLayerId && layer?.enabled
      )
    ) ?? false
  );
};

export const updateWmsSelectedInSettings = (
  settings: Settings,
  wmsId: string,
  layerName: string,
  add: boolean
): Settings => {
  const wmsLayers = getWmsLayersFromSettings(settings);
  const layerIndex = wmsLayers.findIndex((layer) => layer.id === wmsId);

  if (layerIndex === -1) {
    return settings;
  }

  const wmsLayer = wmsLayers[layerIndex];
  const currentSelected = wmsLayer.selected || [];

  let updatedSelected: string[];
  if (add) {
    updatedSelected = currentSelected.includes(layerName)
      ? currentSelected
      : [...currentSelected, layerName];
  } else {
    updatedSelected = currentSelected.filter((name) => name !== layerName);
  }

  const updatedWmsLayers = wmsLayers.map((layer, index) =>
    index === layerIndex ? { ...layer, selected: updatedSelected } : layer
  );

  return {
    ...settings,
    wmsSettings: updatedWmsLayers,
  };
};

export const clearAllWmsSelectedInSettings = (settings: Settings): Settings => {
  const wmsLayers = getWmsLayersFromSettings(settings);

  if (wmsLayers.length === 0) {
    return settings;
  }

  const updatedWmsLayers = wmsLayers.map((layer) => ({
    ...layer,
    selected: [],
  }));

  return {
    ...settings,
    wmsSettings: updatedWmsLayers,
  };
};

/**
 * Hook to handle WMS layer removal (e.g., when a layer fails to load).
 * Updates both the enabled state and the selected array in settings.
 */
export const useRemoveWmsLayer = () => {
  const { messages } = useLocalization();
  const { settings } = useSettings();
  const { toast } = useToast();
  const wmsSettings = useWMSSettings();
  const { mutate: mutateSettings } = useSaveSettings();

  const removeWmsLayer = (layerItem: LayerItem) => {
    let settingsToSave = ToggleWmsLayerInSettings(
      wmsSettings.wmsInfos,
      layerItem.id,
      layerItem.name,
      false,
      settings
    );

    // Find the parent WMS service to get wmsId
    const parentWms = wmsSettings.wmsInfos.find((wms) =>
      wms.layers?.some((layer) => layer.id === layerItem.id)
    );
    if (parentWms) {
      settingsToSave = updateWmsSelectedInSettings(
        settingsToSave,
        parentWms.id,
        layerItem.name,
        false
      );
    }

    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'enable-wms-layer-success-toast',
          title: messages.genericToastTitleSuccess,
          description: `Removed WMS layer, ${layerItem.name}, due to load failure`,
        });
      },
      onError: () => {
        toast({
          className: 'enable-wms-layer-error-toast',
          title: messages.genericToastTitleError,
          description: `Failed to remove WMS layer, ${layerItem.name}, due to load failure`,
          variant: 'destructive',
        });
      },
    });
  };

  return removeWmsLayer;
};

/**
 * Hook providing WMS layer toggle actions.
 * Handles adding/removing layers and updating the `selected` array in settings
 * so the backend knows which WMS layers are active (e.g., for replay).
 */
export const useWmsLayerActions = () => {
  const { settings } = useSettings();
  const { mutate: mutateSettings } = useSaveSettings();
  const { addExistingLayerItem, removeLayer, getLayerById, doesLayerExist } =
    useLayers();
  const { toast } = useToast();
  const { messages } = useLocalization();
  const removeWmsLayer = useRemoveWmsLayer();
  const onWmsToTileLayerWithLayerItem = useWmsToTileLayer(removeWmsLayer);

  const toggleWmsLayer = async (
    wmsInfo: WMSInfo,
    wmsLayerInfo: WMSLayerInfo
  ): Promise<boolean> => {
    const enable = !doesLayerExist(wmsLayerInfo.id);

    if (enable) {
      try {
        const { tileLayer, layerItem } = await onWmsToTileLayerWithLayerItem(
          wmsInfo,
          wmsLayerInfo
        );

        if (!tileLayer || !layerItem) {
          toast({
            className: 'enable-wms-layer-error-toast',
            title: messages.genericToastTitleError,
            description: `Failed to add WMS layer: ${wmsLayerInfo.title}`,
            variant: 'destructive',
          });
          return false;
        }

        addExistingLayerItem(tileLayer, layerItem);

        const settingsToSave = updateWmsSelectedInSettings(
          settings,
          wmsInfo.id,
          wmsLayerInfo.name,
          true
        );
        mutateSettings(settingsToSave);

        toast({
          className: 'enable-wms-layer-success-toast',
          title: messages.genericToastTitleSuccess,
          description: `Added WMS layer: ${wmsLayerInfo.title}`,
        });
        return true;
      } catch (error) {
        toast({
          className: 'enable-wms-layer-error-toast',
          title: messages.genericToastTitleError,
          description: `Failed to add WMS layer: ${wmsLayerInfo.title}`,
          variant: 'destructive',
        });
        return false;
      }
    } else {
      const layerItem = getLayerById(wmsLayerInfo.id);
      if (layerItem) {
        removeLayer(layerItem.layer);

        const settingsToSave = updateWmsSelectedInSettings(
          settings,
          wmsInfo.id,
          wmsLayerInfo.name,
          false
        );
        mutateSettings(settingsToSave);

        toast({
          className: 'enable-wms-layer-success-toast',
          title: messages.genericToastTitleSuccess,
          description: `Removed WMS layer: ${wmsLayerInfo.title}`,
        });
      }
      return true;
    }
  };

  return { toggleWmsLayer, doesLayerExist };
};
